import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { useOfflineMutation } from '../../../hooks/useOfflineMutation';
import { logAccess } from '../../../lib/accessLogs';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import PrintablePrescription from '../../../components/feature/PrintablePrescription';

interface Patient {
  id: string;
  nom: string;
  prenom: string;
}

interface MedecinInfo {
  id: string;
  nom: string;
  prenom: string;
  specialite: string | null;
}

interface SoinRow {
  id: string;
  date: string;
  soin: string;
  tarif: string;
}

export default function CreerPrescriptionPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get('patientId');

  const [patient, setPatient] = useState<Patient | null>(null);
  const [medecinInfo, setMedecinInfo] = useState<MedecinInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prescriptionCree, setPrescriptionCree] = useState(false);
  const [prescriptionId, setPrescriptionId] = useState<string>('');
  const [isPrinting, setIsPrinting] = useState(false);

  // Soins table
  const [soins, setSoins] = useState<SoinRow[]>([
    { id: crypto.randomUUID(), date: '', soin: '', tarif: '' }
  ]);

  // Ordonnance
  const [ordonnance, setOrdonnance] = useState('');

  // Autres champs
  const [dureeTraitement, setDureeTraitement] = useState('');
  const [instructions, setInstructions] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>('');
  const { mutate } = useOfflineMutation<any>();

  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    loadData();
  }, [user, patientId]);

  const loadData = async () => {
    try {
      setLoading(true);

      const { data: medecinData } = await supabase
        .from('medecins')
        .select('id, nom, prenom, specialite')
        .eq('user_id', user?.id)
        .single();

      if (medecinData) {
        setMedecinInfo(medecinData);
      }

      if (patientId) {
        const { data: patientData } = await supabase
          .from('patients')
          .select('id, nom, prenom')
          .eq('id', patientId)
          .single();

        if (patientData) {
          setPatient(patientData);
        }
      }
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSoinRow = () => {
    setSoins(prev => [...prev, { id: crypto.randomUUID(), date: '', soin: '', tarif: '' }]);
  };

  const handleRemoveSoinRow = (id: string) => {
    if (soins.length > 1) {
      setSoins(prev => prev.filter(s => s.id !== id));
    }
  };

  const handleSoinChange = (id: string, field: keyof SoinRow, value: string) => {
    setSoins(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePrintImageReady = () => {
    setTimeout(() => {
      window.print();
      setTimeout(() => setIsPrinting(false), 500);
    }, 300);
  };

  const handlePrint = () => {
    if (!ordonnance.trim()) {
      alert('Veuillez saisir l\'ordonnance avant d\'imprimer');
      return;
    }
    const img = new Image();
    img.src = 'https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/brand/letterhead.webp';
    img.onload = () => setIsPrinting(true);
    img.onerror = () => setIsPrinting(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!ordonnance.trim()) {
      alert('Veuillez saisir l\'ordonnance');
      return;
    }

    if (!patientId || !medecinInfo) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    // Filtrer les soins qui ont au moins un champ rempli
    const soinsFiltres = soins.filter(s => s.date.trim() || s.soin.trim() || s.tarif.trim());

    try {
      setSaving(true);
      const online = navigator.onLine;

      let photoUrl: string | null = null;

      // Upload photo (online only)
      if (online && photoFile) {
        const fileExt = photoFile.name.split('.').pop();
        const fileName = `${Date.now()}.${fileExt}`;
        const filePath = `prescriptions/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('medical-documents')
          .upload(filePath, photoFile);

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from('medical-documents')
          .getPublicUrl(filePath);

        photoUrl = urlData.publicUrl;
      }

      // Création prescription via offline-first
      const prescriptionResult = await mutate({
        table: 'prescriptions',
        operation: 'INSERT',
        data: {
          patient_id: patientId,
          medecin_id: medecinInfo.id,
          medicaments: soinsFiltres.length > 0 ? soinsFiltres.map(s => ({
            date: s.date,
            soin: s.soin,
            tarif: s.tarif ? parseInt(s.tarif, 10) : 0
          })) : null,
          diagnostic: ordonnance.trim(),
          duree_traitement: dureeTraitement.trim() || null,
          instructions: instructions.trim() || null,
          photo_url: photoUrl,
        },
        metadata: {
          summary: `Prescription — ${patient.prenom} ${patient.nom}`,
          performedBy: 'medecin',
        },
        onlineExecutor: async () => {
          const { data: prescriptionData, error } = await supabase
            .from('prescriptions')
            .insert({
              patient_id: patientId,
              medecin_id: medecinInfo.id,
              medicaments: soinsFiltres.length > 0 ? soinsFiltres.map(s => ({
                date: s.date,
                soin: s.soin,
                tarif: s.tarif ? parseInt(s.tarif, 10) : 0
              })) : null,
              diagnostic: ordonnance.trim(),
              duree_traitement: dureeTraitement.trim() || null,
              instructions: instructions.trim() || null,
              photo_url: photoUrl,
            })
            .select()
            .single();

          if (error) throw error;
          return prescriptionData;
        },
      });

      // Log access (online only)
      if (online && user && prescriptionResult?.id) {
        const prescId = prescriptionResult.id;
        await logAccess({
          userId: user.id,
          userRole: 'medecin',
          actionType: 'CREATE',
          resourceType: 'PRESCRIPTION',
          resourceId: prescId,
          patientId: patientId,
          details: {
            patient_nom: patient ? `${patient.prenom} ${patient.nom}` : 'Inconnu',
            soins: soinsFiltres,
            duree_traitement: dureeTraitement.trim(),
            action: 'Création d\'une prescription'
          }
        });
        setPrescriptionId(prescId);
      } else if (prescriptionResult?.id && !prescriptionResult.id.startsWith('pending-')) {
        setPrescriptionId(prescriptionResult.id);
      }

      if (!online) {
        alert('Prescription enregistrée en mode hors-ligne. Synchronisation automatique au retour de la connexion.');
      }

      setPrescriptionCree(true);
    } catch (error) {
      console.error('Erreur création prescription:', error);
      alert('Erreur lors de la création de la prescription');
    } finally {
      setSaving(false);
    }
  };

  const soinsFiltres = soins.filter(s => s.date.trim() || s.soin.trim() || s.tarif.trim());
  const totalTarifs = soinsFiltres.reduce((sum, s) => sum + (parseInt(s.tarif, 10) || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-clinic-blue animate-spin"></i>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-6xl text-red-500"></i>
          <p className="mt-4 text-xl font-semibold text-gray-900">Patient non trouvé</p>
          <button
            onClick={() => navigate('/medecin/rendez-vous')}
            className="mt-6 px-6 py-2 bg-clinic-blue text-white rounded-lg hover:bg-clinic-blue-dark whitespace-nowrap cursor-pointer"
          >
            Retour aux rendez-vous
          </button>
        </div>
      </div>
    );
  }

  const practitionerFullName = medecinInfo
    ? `Dr. ${medecinInfo.prenom} ${medecinInfo.nom}`
    : 'Médecin';

  const todayStr = new Date().toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="no-print">
        <Header />
      </div>

      <div className="bg-white shadow-sm border-b border-gray-200 no-print">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Prescription</h1>
              <p className="mt-2 text-gray-600">
                Patient : {patient.prenom} {patient.nom}
              </p>
            </div>
            <button
              onClick={() => navigate('/medecin/rendez-vous')}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Retour
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 no-print">
        {prescriptionCree ? (
          /* --- SUCCÈS : Prescription créée --- */
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-clinic-blue/15 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-3xl text-clinic-blue"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Prescription créée avec succès !</h2>
              <p className="text-gray-600">
                La prescription a été enregistrée dans le dossier du patient.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={handlePrint}
                className="w-full sm:w-auto px-8 py-3 bg-clinic-blue text-white rounded-lg hover:bg-clinic-blue-dark transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 text-base font-medium"
              >
                <i className="ri-printer-line"></i>
                Imprimer la prescription
              </button>
              <button
                onClick={() => navigate('/medecin/rendez-vous')}
                className="w-full sm:w-auto px-8 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-arrow-left-line"></i>
                Retour aux rendez-vous
              </button>
            </div>
          </div>
        ) : (
          /* --- FORMULAIRE --- */
          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-8">

            {/* ===== TABLEAU DES SOINS ===== */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Soins prescrits</h3>
                  <p className="text-sm text-gray-500 mt-1">Renseignez les soins, dates et tarifs</p>
                </div>
                <button
                  type="button"
                  onClick={handleAddSoinRow}
                  className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-clinic-blue bg-clinic-blue/10 hover:bg-clinic-blue/15 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line"></i>
                  Ajouter un soin
                </button>
              </div>

              <div className="overflow-x-auto border border-gray-200 rounded-xl">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 w-[170px]">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200">Soin / Acte</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 w-[140px]">Tarif (FCFA)</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200 w-[50px]"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {soins.map((soin, index) => (
                      <tr key={soin.id} className={`border-b border-gray-100 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                        <td className="px-4 py-2.5">
                          <input
                            type="date"
                            value={soin.date}
                            onChange={(e) => handleSoinChange(soin.id, 'date', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-clinic-blue focus:border-transparent"
                          />
                        </td>
                        <td className="px-4 py-2.5">
                          <input
                            type="text"
                            value={soin.soin}
                            onChange={(e) => handleSoinChange(soin.id, 'soin', e.target.value)}
                            placeholder="Ex: Consultation, Pansement, Injection..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-clinic-blue focus:border-transparent"
                          />
                        </td>
                        <td className="px-4 py-2.5">
                          <input
                            type="number"
                            value={soin.tarif}
                            onChange={(e) => handleSoinChange(soin.id, 'tarif', e.target.value)}
                            placeholder="0"
                            min="0"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-clinic-blue focus:border-transparent"
                          />
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          {soins.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveSoinRow(soin.id)}
                              className="w-8 h-8 flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {soinsFiltres.length > 0 && totalTarifs > 0 && (
                    <tfoot>
                      <tr className="bg-clinic-blue/5">
                        <td className="px-4 py-3"></td>
                        <td className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Total :</td>
                        <td className="px-4 py-3 text-sm font-bold text-clinic-blue-dark">{totalTarifs.toLocaleString('fr-FR')} FCFA</td>
                        <td className="px-4 py-3"></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>

            {/* ===== ORDONNANCE ===== */}
            <div className="border-t border-gray-200 pt-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Ordonnance <span className="text-red-500">*</span></h3>
                <p className="text-sm text-gray-500 mt-1">Rédigez l'ordonnance complète pour le patient</p>
              </div>
              <textarea
                value={ordonnance}
                onChange={(e) => setOrdonnance(e.target.value)}
                rows={8}
                placeholder="Rédigez ici l'ordonnance détaillée : médicaments, posologies, durée, recommandations..."
                className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-clinic-blue focus:border-transparent resize-none text-sm leading-relaxed"
                required
              />
            </div>

            {/* ===== DURÉE DU TRAITEMENT ===== */}
            <div className="border-t border-gray-200 pt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Durée du traitement <span className="text-gray-500">(facultatif)</span>
              </label>
              <input
                type="text"
                value={dureeTraitement}
                onChange={(e) => setDureeTraitement(e.target.value)}
                placeholder="Ex: 7 jours, 2 semaines, 1 mois..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-clinic-blue focus:border-transparent text-sm"
              />
            </div>

            {/* ===== INSTRUCTIONS PARTICULIÈRES ===== */}
            <div className="border-t border-gray-200 pt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Instructions particulières <span className="text-gray-500">(facultatif)</span>
              </label>
              <textarea
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                rows={3}
                placeholder="Précautions, contre-indications, conseils..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-clinic-blue focus:border-transparent resize-none text-sm"
                maxLength={500}
              />
            </div>

            {/* ===== PHOTO ===== */}
            <div className="border-t border-gray-200 pt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Photo de l'ordonnance <span className="text-gray-500">(facultatif)</span>
              </label>
              <div className="space-y-3">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-clinic-blue/10 file:text-clinic-blue-dark hover:file:bg-clinic-blue/15 cursor-pointer"
                />
                {photoPreview && (
                  <div className="relative inline-block">
                    <img
                      src={photoPreview}
                      alt="Aperçu"
                      className="max-w-xs h-auto rounded-lg border border-gray-300"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setPhotoFile(null);
                        setPhotoPreview('');
                      }}
                      className="absolute -top-2 -right-2 w-7 h-7 flex items-center justify-center bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors cursor-pointer"
                    >
                      <i className="ri-close-line text-sm"></i>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* ===== BOUTONS ===== */}
            <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/medecin/rendez-vous')}
                className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handlePrint}
                disabled={!ordonnance.trim()}
                className="px-6 py-2.5 bg-white text-clinic-blue border-2 border-clinic-blue rounded-lg hover:bg-clinic-blue/10 transition-colors whitespace-nowrap disabled:opacity-50 cursor-pointer flex items-center gap-2"
              >
                <i className="ri-printer-line"></i>
                Imprimer
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-8 py-2.5 bg-clinic-blue text-white rounded-lg hover:bg-clinic-blue-dark transition-colors whitespace-nowrap disabled:opacity-50 cursor-pointer font-medium"
              >
                {saving ? (
                  <span className="flex items-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Création en cours...
                  </span>
                ) : (
                  'Créer la prescription'
                )}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Print container */}
      {isPrinting && (
        <div className="print-container">
          <PrintablePrescription
            patientName={`${patient.prenom} ${patient.nom}`}
            practitionerName={practitionerFullName}
            practitionerTitle={medecinInfo?.specialite || undefined}
            date={todayStr}
            ordonnance={ordonnance}
            dureeTraitement={dureeTraitement || undefined}
            instructions={instructions || undefined}
            verificationUrl={prescriptionId ? `${window.location.origin}/verifier-document?type=prescription&id=${prescriptionId}` : undefined}
            onReady={handlePrintImageReady}
          />
        </div>
      )}

      <div className="no-print">
        <Footer />
      </div>
    </div>
  );
}